import { useState } from "react";

import Markdown from "../Markdown";
import styles from "./WelcomeMessage.module.scss";

interface WarningMessageProps {
  message: string | undefined;
  personaId: number | string;
}

function getStorageKey(personaId: number | string): string {
  return `warning-message-collapsed-persona-${personaId}`;
}

function getTitle(message: string): string {
  const firstLine = message.trim().split("\n")[0];
  return firstLine.replace(/^#+\s*/, "");
}

function getBody(message: string): string {
  const lines = message.trim().split("\n");
  return lines.slice(1).join("\n").trim();
}

export default function WarningMessage({
  message,
  personaId,
}: WarningMessageProps) {
  const [prevPersonaId, setPrevPersonaId] = useState(personaId);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem(getStorageKey(personaId));
    return stored === "true";
  });

  // Reset collapsed state when persona changes
  if (prevPersonaId !== personaId) {
    setPrevPersonaId(personaId);
    const stored = localStorage.getItem(getStorageKey(personaId));
    setIsCollapsed(stored === "true");
  }

  if (!message) {
    return null;
  }

  const handleToggle = () => {
    const newValue = !isCollapsed;
    setIsCollapsed(newValue);
    localStorage.setItem(getStorageKey(personaId), String(newValue));
  };

  return (
    <div className={`pa3 mb3 br2 ${styles.warningMessage}`}>
      <button
        className={styles.warningToggle}
        onClick={handleToggle}
        aria-label={isCollapsed ? "Expand warning" : "Collapse warning"}
        aria-expanded={!isCollapsed}
      >
        <span className={styles.toggleArrow}>{isCollapsed ? "▶" : "▼"}</span>
        <span className="b">{getTitle(message)}</span>
      </button>
      {!isCollapsed && (
        <div className={styles.warningContent}>
          <Markdown content={getBody(message)} />
        </div>
      )}
    </div>
  );
}
